import { Entry } from 'contentful';
import { PageProps } from 'libs/contentful/queries/page/props';
import { RelatedArticleFlexibleProps, RelatedArticleProps } from 'components/ArticleGrid/types';
import { ResourceProps } from 'libs/contentful/types';
import { ENTRY_NATIVE_TYPENAMES } from 'utils/entryNativeTypenames';
import type { ModuleTypeName } from 'utils/modules';
import { buildIncludesMap, isMediaAssetLike, isTypenameObject } from './type-guards';
import { annotateModule, flattenModules, removeUnresolvedCollectionItems, wrapCollection } from './page-structure';
import {
  applyEditorialFieldNormalization,
  annotateNestedExperiments,
  applyOptionalFieldNormalization,
  hydrateResourceFieldsFromIncludes,
  normalizeArticleGridFlexibleBlogItems,
  normalizeCalloutIconCardLinks,
  normalizeCardsIconItemCardIcon,
  normalizeCollections,
  normalizeContentBlockEmbeddedDisclosures,
  normalizeCtaLinks,
  normalizeDisclosureAddOnDisclosures,
  normalizeDisclosureGroupingsDisclosures,
  normalizeDisclosuresInToggleItemAccordionCopy,
  normalizeInternalPageLinks,
  normalizeNavMainLinks,
  normalizeNavTopBarLink,
  normalizePageScopedFields,
  normalizeUndefinedToNull,
  removeExperimentFields,
  removeSysFromAssetFields,
  removeSysFromContentTypes,
  stripCardHeadlineRteFromBentoBoxLargeCard,
  stripCardTypenameInCardCarousel,
  stripSysFromHomepageHeroImages,
  walkEntryGraph,
} from './normalizers';
import { adaptAsset, adaptEntry, resetAdaptedCache, resolveAssetSource, resolveEntrySource } from './transforms';

export interface AdaptOptions {
  preserveExperimentFields?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getEntryFields(value: unknown): Record<string, unknown> | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  return isRecord(value.fields) ? value.fields : undefined;
}

function getContentTypeId(value: unknown): string | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const sys = value.sys;
  if (!isRecord(sys)) {
    return undefined;
  }

  const contentType = sys.contentType;
  if (!isRecord(contentType) || !isRecord(contentType.sys)) {
    return undefined;
  }

  return typeof contentType.sys.id === 'string' ? contentType.sys.id : undefined;
}

function isSectionCustomQueryStringParameters(obj: Record<string, unknown>): boolean {
  return obj.__typename === 'SectionCustomQueryStringParameters';
}

/** Normalizes the section's param list to customQueryStringParametersCollection: { items: [...] }. */
function normalizeSectionCustomQueryStringParametersCollection(section: Record<string, unknown>): void {
  let items: Array<Record<string, unknown>> | undefined;
  if (Array.isArray(section.customQueryStringParameters)) {
    items = section.customQueryStringParameters as Array<Record<string, unknown>>;
    delete section.customQueryStringParameters;
  } else if (
    section.customQueryStringParameters &&
    typeof section.customQueryStringParameters === 'object' &&
    Array.isArray((section.customQueryStringParameters as { items?: unknown[] }).items)
  ) {
    items = (section.customQueryStringParameters as { items: unknown[] }).items as Array<Record<string, unknown>>;
    section.customQueryStringParametersCollection = wrapCollection(items);
    delete section.customQueryStringParameters;
    return;
  } else if (Array.isArray(section.customQueryStringParametersCollection)) {
    items = section.customQueryStringParametersCollection as Array<Record<string, unknown>>;
    delete section.customQueryStringParametersCollection;
  } else if (
    section.customQueryStringParametersCollection &&
    typeof section.customQueryStringParametersCollection === 'object'
  ) {
    const col = section.customQueryStringParametersCollection as { items?: unknown[] };
    items = Array.isArray(col.items) ? (col.items as Array<Record<string, unknown>>) : undefined;
  }
  section.customQueryStringParametersCollection = wrapCollection(items ?? []);
}

/**
 * Adapts a REST Entry to PageProps format.
 * This is the main entry point for the adapter.
 *
 * @param entry - The REST SDK Entry response
 * @param includes - The includes array from the REST SDK response
 * @returns PageProps compatible with downstream transformers
 */
export function adaptRESTtoPageProps(
  entry: Entry,
  includes?: { Entry?: unknown[]; Asset?: unknown[] },
  options?: AdaptOptions,
): PageProps;
export function adaptRESTtoPageProps(
  entry: Entry,
  includes: { Entry?: unknown[]; Asset?: unknown[] } = {},
  options?: AdaptOptions,
): unknown {
  // Clear the adapted cache for each new page adaptation
  resetAdaptedCache();

  // Build includes map for link resolution
  const includesMap = buildIncludesMap(includes);
  // Ensure the root page entry can be resolved via Link objects (it is not guaranteed to appear in includes.Entry)
  if (entry?.sys?.id && typeof entry.sys.id === 'string') {
    includesMap.entries.set(entry.sys.id, entry);
  }

  // First, adapt the entry structure
  // Pass wrapCollections: false for page entry - page-level collections are handled below
  // preserveEntryNativeModules: true keeps allowlisted typenames in raw Entry shape;
  // non-allowlisted modules continue to flatten for dispatch via `moduleRegistry`.
  const processed = new WeakSet<object>();
  const adapted = adaptEntry(entry, includesMap, processed, false, false, true) as Record<string, unknown>;

  // Convert array fields to collections
  if (adapted.modules && Array.isArray(adapted.modules)) {
    // Keep modules as array for now, will be flattened below
  }

  // Wrap collections - always create even if empty to match GraphQL
  // Note: headerBanner items are modules and get hasExperiments
  if (adapted.headerBanner && Array.isArray(adapted.headerBanner)) {
    adapted.headerBannerCollection = wrapCollection(
      adapted.headerBanner.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.headerBanner; // Remove original array
  } else {
    adapted.headerBannerCollection = { items: [] };
  }

  // Note: disclosuresCollection items ARE treated as modules and get hasExperiments
  if (adapted.disclosures && Array.isArray(adapted.disclosures)) {
    adapted.disclosuresCollection = wrapCollection(
      adapted.disclosures.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.disclosures; // Remove original array
  } else {
    adapted.disclosuresCollection = { items: [] };
  }

  // disclosuresBottomCollection - items ARE treated as modules and get hasExperiments (same as disclosuresCollection)
  if (adapted.disclosuresBottom && Array.isArray(adapted.disclosuresBottom)) {
    adapted.disclosuresBottomCollection = wrapCollection(
      adapted.disclosuresBottom.map(item => annotateModule(item as Record<string, unknown>)),
    );
    delete adapted.disclosuresBottom; // Remove original array
  } else {
    adapted.disclosuresBottomCollection = { items: [] };
  }

  // bottomModulesCollection (for PageBlogPostFlexible pages only) - modules need annotation
  // Always create this collection for the relevant page type, even if empty - GraphQL returns { items: [] } when undefined
  if (adapted.__typename === 'PageBlogPostFlexible') {
    if (adapted.bottomModules && Array.isArray(adapted.bottomModules)) {
      adapted.bottomModulesCollection = wrapCollection(
        adapted.bottomModules
          .filter(item => isTypenameObject(item as Record<string, unknown>))
          .map(item => annotateModule(item as Record<string, unknown>)),
      );
      delete adapted.bottomModules; // Remove original array
    } else {
      adapted.bottomModulesCollection = { items: [] };
    }
  }

  // relatedArticlesManualCollection for PageBlogPostFlexible
  // REST returns relatedArticlesManual as a plain array; GraphQL returns relatedArticlesManualCollection: { items: [...] }
  if (adapted.__typename === 'PageBlogPostFlexible') {
    if (adapted.relatedArticlesManual && Array.isArray(adapted.relatedArticlesManual)) {
      const articles = adapted.relatedArticlesManual as Array<Record<string, unknown>>;
      adapted.relatedArticlesManual = wrapCollection(articles);
      // Ensure each item has articleTitle/articleImage and categories for RelatedArticlesSidebar
      const collection = adapted.relatedArticlesManual as { items: Array<Record<string, unknown>> };
      for (const item of collection.items) {
        const meta = item?.contentfulMetadata as { concepts?: Array<{ id: string }> } | undefined;
        item.categories = meta?.concepts?.length ? { items: meta.concepts } : { items: [] };
        if (item?.__typename === 'PageBlogPostFlexible' && item.hero && typeof item.hero === 'object') {
          const hero = item.hero as Record<string, unknown>;
          item.articleTitle = hero.headline != null ? { headline: hero.headline } : null;
          item.articleImage = hero.desktopMedia != null ? { desktopMedia: hero.desktopMedia } : null;
        }
      }
    } else {
      adapted.relatedArticlesManual = { items: [] };
    }
  }

  // customQueryStringParameters: inline array → customQueryStringParametersCollection, or section reference (Section Custom Query String Parameters) for Ninetailed
  const rawQsp = adapted.customQueryStringParameters;
  const firstElement =
    rawQsp && Array.isArray(rawQsp) && rawQsp.length > 0 ? (rawQsp[0] as Record<string, unknown>) : null;
  const singleSection =
    firstElement && isSectionCustomQueryStringParameters(firstElement)
      ? firstElement
      : rawQsp && typeof rawQsp === 'object' && isSectionCustomQueryStringParameters(rawQsp as Record<string, unknown>)
        ? (rawQsp as Record<string, unknown>)
        : null;

  if (singleSection) {
    normalizeSectionCustomQueryStringParametersCollection(singleSection);
    if (!singleSection.__typename) {
      singleSection.__typename = 'SectionCustomQueryStringParameters';
    }
    adapted.customQueryStringParameters = annotateModule(singleSection);
    adapted.customQueryStringParametersCollection = { items: [] };
  } else if (rawQsp && Array.isArray(rawQsp)) {
    adapted.customQueryStringParametersCollection = wrapCollection(rawQsp as Array<Record<string, unknown>>);
    delete adapted.customQueryStringParameters;
  } else {
    if (adapted.customQueryStringParameters && !Array.isArray(adapted.customQueryStringParameters)) {
      adapted.customQueryStringParameters = null;
    }
    if (!adapted.customQueryStringParametersCollection) {
      adapted.customQueryStringParametersCollection = { items: [] };
    }
  }

  // Annotate top-level sections
  if (adapted.nav) {
    if (isTypenameObject(adapted.nav)) {
      adapted.nav = annotateModule(adapted.nav);
    } else {
      adapted.nav = null;
    }
  }
  if (adapted.hero) {
    if (isTypenameObject(adapted.hero)) {
      adapted.hero = annotateModule(adapted.hero);
    } else {
      adapted.hero = null;
    }
  }
  if (adapted.footer) {
    if (isTypenameObject(adapted.footer)) {
      adapted.footer = annotateModule(adapted.footer);
    } else {
      adapted.footer = null;
    }
  }
  if (adapted.pageVisualSettings) {
    if (isTypenameObject(adapted.pageVisualSettings)) {
      adapted.pageVisualSettings = annotateModule(adapted.pageVisualSettings);
    } else {
      adapted.pageVisualSettings = null;
    }
  }

  // Flatten modules
  const flattenedModules = flattenModules(adapted).filter(isTypenameObject);
  const annotatedModules = flattenedModules.map(m => {
    const typename = m.__typename;
    // Entry-native modules pass through untouched. Allowlist is empty until
    // EVOLUTION-2967 begins flipping individual components.
    if (typeof typename === 'string' && ENTRY_NATIVE_TYPENAMES.has(typename as ModuleTypeName)) {
      return m;
    }
    return annotateModule(m);
  });
  adapted.modulesCollection = wrapCollection(annotatedModules);

  // Remove original module array fields that have been flattened
  delete adapted.modules;

  // Set page-level hasExperiments if any module or top-level section has experiments
  const moduleHasExperiments = annotatedModules.some(m => m.hasExperiments === true);
  const navHasExperiments = adapted.nav ? (adapted.nav as Record<string, unknown>).hasExperiments === true : false;
  const heroHasExperiments = adapted.hero ? (adapted.hero as Record<string, unknown>).hasExperiments === true : false;
  const footerHasExperiments = adapted.footer
    ? (adapted.footer as Record<string, unknown>).hasExperiments === true
    : false;
  const pageVisualSettingsHasExperiments = adapted.pageVisualSettings
    ? (adapted.pageVisualSettings as Record<string, unknown>).hasExperiments === true
    : false;
  const customQueryStringParametersHasExperiments = adapted.customQueryStringParameters
    ? (adapted.customQueryStringParameters as Record<string, unknown>).hasExperiments === true
    : false;
  const disclosuresHasExperiments =
    adapted.disclosuresCollection &&
    (adapted.disclosuresCollection as { items: Array<Record<string, unknown>> }).items?.some(
      m => m.hasExperiments === true,
    );
  const headerBannerHasExperiments =
    adapted.headerBannerCollection &&
    (adapted.headerBannerCollection as { items: Array<Record<string, unknown>> }).items?.some(
      m => m.hasExperiments === true,
    );

  adapted.hasExperiments =
    moduleHasExperiments ||
    navHasExperiments ||
    heroHasExperiments ||
    footerHasExperiments ||
    pageVisualSettingsHasExperiments ||
    customQueryStringParametersHasExperiments ||
    disclosuresHasExperiments ||
    headerBannerHasExperiments;

  // Ensure optional fields that GraphQL returns as null are explicitly set
  if (adapted.__typename === 'Page' && !('pageColor' in adapted)) {
    adapted.pageColor = null;
  }
  if (!('footerColor' in adapted)) {
    adapted.footerColor = null;
  }
  if (!('showFooterKeyline' in adapted)) {
    adapted.showFooterKeyline = null;
  }
  if (adapted.__typename === 'Page' && !('pageVisualSettings' in adapted)) {
    adapted.pageVisualSettings = null;
  }
  if (adapted.__typename === 'Page' && !('customQueryStringParameters' in adapted)) {
    adapted.customQueryStringParameters = null;
  }
  if (!('seoAllowSearchEnginesToIndex' in adapted)) {
    adapted.seoAllowSearchEnginesToIndex = null;
  }
  if (!('seoAllowSearchEnginesToFollow' in adapted)) {
    adapted.seoAllowSearchEnginesToFollow = null;
  }
  if (!('hero' in adapted)) {
    adapted.hero = null;
  }
  if (!('nav' in adapted)) {
    adapted.nav = null;
  }
  if (!('footer' in adapted)) {
    adapted.footer = null;
  }
  if ((adapted.__typename === 'Page' || adapted.__typename === 'PageAuthor') && !('schema' in adapted)) {
    adapted.schema = null;
  }
  if (!('seoCanonical' in adapted)) {
    adapted.seoCanonical = null;
  }
  if (!('seoDescription' in adapted)) {
    adapted.seoDescription = null;
  }
  if (!('seoTitle' in adapted)) {
    adapted.seoTitle = null;
  }
  if (!('seoOpenGraphTitle' in adapted)) {
    adapted.seoOpenGraphTitle = null;
  }
  if (!('seoOpenGraphDescription' in adapted)) {
    adapted.seoOpenGraphDescription = null;
  }
  if (!('seoOpenGraphImage' in adapted)) {
    adapted.seoOpenGraphImage = null;
  }

  // AgreementPage-specific null defaults
  if (adapted.__typename === 'PageAgreement') {
    if (!('heading' in adapted)) adapted.heading = null;
    if (!('finePrint' in adapted)) adapted.finePrint = null;
    if (!('showTableOfContents' in adapted)) adapted.showTableOfContents = null;
    if (!('effectiveDate' in adapted)) adapted.effectiveDate = null;
    if (!('revisedDate' in adapted)) adapted.revisedDate = null;
    if (!('bank' in adapted)) adapted.bank = null;
  }

  // Drop unresolved collection items (sys.id present but missing __typename), matching GraphQL fixture behavior.
  removeUnresolvedCollectionItems(adapted);

  // Some recorded fixtures contain Resource fields that may be omitted during adaptation; hydrate from includes.
  hydrateResourceFieldsFromIncludes(adapted, includesMap, processed);

  // Normalize PageBlogPostFlexible entries within ArticleGrid.articlesCollection
  // Pass includesMap so we can look up raw hero data for circular reference cases
  normalizeArticleGridFlexibleBlogItems(adapted, includesMap);

  // PageSection uses modulesCollection in GraphQL
  if (adapted.modulesCollection && typeof adapted.modulesCollection === 'object') {
    for (const item of (adapted.modulesCollection as { items?: unknown[] }).items ?? []) {
      if (!item || typeof item !== 'object' || Array.isArray(item)) continue;
      const obj = item as Record<string, unknown>;
      if (obj.__typename === 'PageSection') {
        const modules = obj.modules;
        if (modules && typeof modules === 'object' && !Array.isArray(modules) && 'items' in modules) {
          obj.modulesCollection = modules;
          delete obj.modules;
        }
      }
    }
  }

  // Combine all per-node normalizations into a single graph walk.
  // Uses a root-skip closure for contentfulMetadata (GraphQL preserves it on the page but not nested entries).
  const root = adapted;
  walkEntryGraph(adapted, [
    applyEditorialFieldNormalization,
    applyOptionalFieldNormalization,
    annotateNestedExperiments,
    ...(options?.preserveExperimentFields ? [] : [removeExperimentFields]),
    normalizeDisclosureGroupingsDisclosures,
    normalizeDisclosureAddOnDisclosures,
    normalizeContentBlockEmbeddedDisclosures,
    removeSysFromAssetFields,
    removeSysFromContentTypes,
    normalizeCardsIconItemCardIcon,
    normalizeNavMainLinks,
    normalizeNavTopBarLink,
    normalizeCtaLinks,
    normalizeInternalPageLinks,
    normalizeCalloutIconCardLinks,
    normalizeCollections,
    stripCardTypenameInCardCarousel,
    stripCardHeadlineRteFromBentoBoxLargeCard,
    normalizeDisclosuresInToggleItemAccordionCopy,
    stripSysFromHomepageHeroImages,
    entry => {
      if (entry !== root && 'contentfulMetadata' in entry) {
        delete entry.contentfulMetadata;
      }
    },
  ]);

  // Apply page-slug-aware and context-aware graph mutations in a single traversal.
  normalizePageScopedFields(adapted);

  // Normalize undefined to null to match GraphQL behavior (mutates in-place)
  normalizeUndefinedToNull(adapted);

  if (options?.preserveExperimentFields) {
    const adaptedEntries = new Map<string, unknown>();
    for (const [id, rawEntry] of includesMap.entries) {
      const adaptedEntry = adaptEntry(rawEntry, includesMap, processed, true, false, true);
      if (adaptedEntry != null && typeof adaptedEntry === 'object') {
        adaptedEntries.set(id, adaptedEntry);
      }
    }
    adapted._includesMap = { entries: adaptedEntries, assets: includesMap.assets };
  }

  return adapted;
}

/**
 * Result type for adaptEntriesForArticleGrid - can contain any of the supported article types.
 */
export type ArticleGridItem = RelatedArticleFlexibleProps | RelatedArticleProps | ResourceProps;

/**
 * Resolved asset shape that matches MediaAsset requirements.
 */
interface ResolvedAsset {
  url: string;
  title: string;
  contentType: string;
  description?: string;
  width?: number;
  height?: number;
}

/**
 * Helper to resolve an asset link to a MediaAsset-like object.
 */
function resolveAsset(assetLink: unknown, includesMap: ReturnType<typeof buildIncludesMap>): ResolvedAsset | undefined {
  const resolvedAsset = resolveAssetSource(assetLink, includesMap);
  if (!resolvedAsset) {
    return undefined;
  }

  const adaptedAsset = isMediaAssetLike(resolvedAsset) ? resolvedAsset : adaptAsset(resolvedAsset);
  if (!isRecord(adaptedAsset)) {
    return undefined;
  }

  if (
    typeof adaptedAsset.url !== 'string' ||
    typeof adaptedAsset.title !== 'string' ||
    typeof adaptedAsset.contentType !== 'string'
  ) {
    return undefined;
  }

  return {
    url: adaptedAsset.url,
    title: adaptedAsset.title,
    contentType: adaptedAsset.contentType,
    description: typeof adaptedAsset.description === 'string' ? adaptedAsset.description : undefined,
    width: typeof adaptedAsset.width === 'number' ? adaptedAsset.width : undefined,
    height: typeof adaptedAsset.height === 'number' ? adaptedAsset.height : undefined,
  };
}

function resolveInternalPage(
  value: unknown,
  includesMap: ReturnType<typeof buildIncludesMap>,
): ResourceProps['resourcePage'] | undefined {
  const pageEntry = resolveEntrySource(value, includesMap);
  const pageFields = getEntryFields(pageEntry);
  if (!pageFields) {
    return undefined;
  }

  const slug = pageFields.slug;
  const sys = pageEntry?.sys;
  if (typeof slug !== 'string' || !isRecord(sys) || typeof sys.id !== 'string') {
    return undefined;
  }

  return {
    slug,
    sys: { id: sys.id },
  };
}

function resolveAdaptedEntry(
  value: unknown,
  includesMap: ReturnType<typeof buildIncludesMap>,
): Record<string, unknown> | undefined {
  const rawEntry = resolveEntrySource(value, includesMap);
  if (!rawEntry) {
    return undefined;
  }

  const adapted = adaptEntry(rawEntry, includesMap, new WeakSet<object>(), true);
  return isRecord(adapted) ? adapted : undefined;
}

/**
 * Adapts a PageBlogPostFlexible entry to RelatedArticleFlexibleProps.
 */
function adaptFlexibleBlogPost(
  entry: Entry,
  includesMap: ReturnType<typeof buildIncludesMap>,
): RelatedArticleFlexibleProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  const slug = fields.slug as string | undefined;
  if (!slug) return null;

  const publishDate = fields.publishDate as string | undefined;

  // Resolve hero data - articleTitle requires headline
  let articleTitle: RelatedArticleFlexibleProps['articleTitle'] | undefined;
  let articleImage: RelatedArticleFlexibleProps['articleImage'] | undefined;

  const rawHero = resolveEntrySource(fields.hero, includesMap);
  if (getContentTypeId(rawHero) === 'heroConsolidated') {
    const adaptedHero = resolveAdaptedEntry(rawHero, includesMap);
    if (adaptedHero?.headline) {
      articleTitle = {
        headline: adaptedHero.headline as RelatedArticleFlexibleProps['articleTitle']['headline'],
      };
    }

    const resolvedMedia = resolveAsset(getEntryFields(rawHero)?.desktopMedia, includesMap);
    if (resolvedMedia) {
      articleImage = { desktopMedia: resolvedMedia };
    }
  }

  // Skip entries without a valid hero/headline
  if (!articleTitle) return null;

  const metadata = entry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
  const categories = metadata?.concepts ? { items: metadata.concepts.map(c => ({ id: c.sys.id })) } : { items: [] };

  return {
    __typename: 'PageBlogPostFlexible',
    articleTitle,
    articleImage,
    slug,
    categories,
    publishDate,
    sys: { id: sys.id, createdAt: sys.createdAt },
  };
}

/**
 * Adapts a BlogPostPage (blogPage) entry to RelatedArticleProps.
 */
function adaptBlogPostPage(entry: Entry, includesMap: ReturnType<typeof buildIncludesMap>): RelatedArticleProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  const slug = fields.slug as string | undefined;
  if (!slug) return null;

  const publishDate = fields.publishDate as string | undefined;

  // BlogPostPage has articleTitle directly as a rich text field
  const articleTitle = fields.articleTitle;
  if (!articleTitle) return null;

  // Resolve articleImage asset
  const articleImage = resolveAsset(fields.articleImage, includesMap);

  const metadata = entry.metadata as { concepts?: Array<{ sys: { id: string } }> } | undefined;
  const categories = metadata?.concepts ? { items: metadata.concepts.map(c => ({ id: c.sys.id })) } : { items: [] };

  return {
    __typename: 'RelatedArticle',
    articleTitle: articleTitle as RelatedArticleProps['articleTitle'],
    articleImage: articleImage as RelatedArticleProps['articleImage'],
    slug,
    categories,
    publishDate,
    sys: { id: sys.id, createdAt: sys.createdAt },
  };
}

/**
 * Adapts a Resource entry to ResourceProps.
 */
function adaptResource(entry: Entry, includesMap: ReturnType<typeof buildIncludesMap>): ResourceProps | null {
  const fields = entry.fields as Record<string, unknown>;
  const sys = entry.sys;

  if (!fields || !sys?.id) return null;

  // Resource requires resourceCopy
  const resourceCopy = fields.resourceCopy;
  if (!resourceCopy) return null;

  // Resolve resourceIcon asset
  const resourceIcon = resolveAsset(fields.resourceIcon, includesMap);
  const resourcePage = resolveInternalPage(fields.resourcePage, includesMap);

  return {
    __typename: 'Resource',
    resourceCopy: resourceCopy as ResourceProps['resourceCopy'],
    resourceIcon: resourceIcon as ResourceProps['resourceIcon'],
    resourceLabelRte: fields.resourceLabelRte as ResourceProps['resourceLabelRte'],
    resourcePage,
    url: fields.url as string | undefined,
    sys: { id: sys.id, createdAt: sys.createdAt },
  };
}

/**
 * Adapts an array of REST entries to the shapes expected by ArticleGrid.
 * Supports multiple content types: PageBlogPostFlexible, BlogPostPage (blogPage), and Resource.
 *
 * @param entries - Array of REST SDK Entry responses
 * @param includes - The includes object from the REST SDK response
 * @returns Array of article items compatible with ArticleGrid
 */
export function adaptEntriesForArticleGrid(
  entries: Entry[],
  includes: { Entry?: unknown[]; Asset?: unknown[] } = {},
): ArticleGridItem[] {
  resetAdaptedCache();
  const includesMap = buildIncludesMap(includes);

  // Add all entries to the includes map so they can be resolved
  for (const entry of entries) {
    if (entry?.sys?.id && typeof entry.sys.id === 'string') {
      includesMap.entries.set(entry.sys.id, entry);
    }
  }

  const results: ArticleGridItem[] = [];

  for (const entry of entries) {
    const sys = entry.sys as { contentType?: { sys?: { id?: string; createdAt?: string } } } | undefined;
    const contentTypeId = sys?.contentType?.sys?.id;

    let adapted: ArticleGridItem | null = null;

    switch (contentTypeId) {
      case 'pageBlogPostFlexible':
        adapted = adaptFlexibleBlogPost(entry, includesMap);
        break;
      case 'blogPage':
        adapted = adaptBlogPostPage(entry, includesMap);
        break;
      case 'resource':
        adapted = adaptResource(entry, includesMap);
        break;
    }

    if (adapted) {
      results.push(adapted);
    }
  }

  return results;
}
